/**
 * Kunden und Lieferanten.
 *
 * Eine Liste für beides, weil die meisten Adressen ohnehin beides sind oder
 * die Rolle wechseln. Zwei getrennte Listen hießen dieselbe Anschrift
 * zweimal pflegen — und irgendwann zwei verschiedene Anschriften für
 * denselben Empfänger.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog } from '../bausteine/Dialog';
import { Auswahlfeld, Textbereich, Textfeld } from '../bausteine/Feld';
import { Seite } from '../bausteine/Layout';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { Laedt, Leer } from '../bausteine/Zustand';
import { useAbruf, useVorgang } from '../lib/abruf';
import { alsAbfrage, api } from '../lib/api';
import { useSitzung } from '../sitzung/SitzungKontext';
import type { Kontakt, KontaktListe, KontaktTyp } from '../lib/typen';

const TYPEN = [
  { wert: 'beides' as KontaktTyp, text: 'Kunde und Lieferant' },
  { wert: 'kunde' as KontaktTyp, text: 'Kunde' },
  { wert: 'lieferant' as KontaktTyp, text: 'Lieferant' },
];

export function Kontakte(): ReactNode {
  const [filter, setzeFilter] = useSearchParams();
  const { darfSchreiben } = useSitzung();
  const lese = (name: string): string => filter.get(name) ?? '';

  const [bearbeitet, setzeBearbeitet] = useState<Kontakt | 'neu' | null>(null);

  function aendere(name: string, wert: string): void {
    const neu = new URLSearchParams(filter);
    if (wert === '') neu.delete(name);
    else neu.set(name, wert);
    setzeFilter(neu, { replace: true });
  }

  const pfad = useMemo(
    () => `/api/kontakte${alsAbfrage({ suche: lese('suche'), typ: lese('typ'), proSeite: 200 })}`,
    [filter.toString()],
  );

  const liste = useAbruf<KontaktListe>(pfad);

  return (
    <Seite
      titel="Kontakte"
      untertitel={liste.daten ? `${liste.daten.gesamt} erfasst` : undefined}
      aktionen={
        darfSchreiben ? (
          <button type="button" className="knopf haupt" onClick={() => setzeBearbeitet('neu')}>
            Kontakt anlegen
          </button>
        ) : null
      }
    >
      <div className="karte">
        <div className="filterleiste">
          <div className="feld suchfeld">
            <label htmlFor="suche">Suche</label>
            <input
              id="suche"
              type="search"
              value={lese('suche')}
              placeholder="Name, Ort oder Nummer"
              onChange={(e) => aendere('suche', e.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="typ">Rolle</label>
            <select id="typ" value={lese('typ')} onChange={(e) => aendere('typ', e.target.value)}>
              <option value="">alle</option>
              <option value="kunde">Kunden</option>
              <option value="lieferant">Lieferanten</option>
            </select>
          </div>
        </div>

        {liste.fehler ? (
          <div className="karte-koerper">
            <Fehlermeldung text={liste.fehler} />
          </div>
        ) : !liste.daten ? (
          <Laedt was="Kontakte werden geladen" />
        ) : liste.daten.kontakte.length === 0 ? (
          <Leer
            titel="Kein Kontakt"
            hinweis="Für eine Rechnung braucht es einen Empfänger mit Anschrift."
          />
        ) : (
          <div className="tabelle-rahmen">
            <table className="tabelle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Anschrift</th>
                  <th className="schmal">Rolle</th>
                  <th>Kontakt</th>
                  <th className="schmal">Zahlungsziel</th>
                  {darfSchreiben ? <th className="schmal" /> : null}
                </tr>
              </thead>
              <tbody>
                {liste.daten.kontakte.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <span className="haupttext">{k.name}</span>
                      {k.zusatz ? <div className="nebentext">{k.zusatz}</div> : null}
                      {k.nummer ? <div className="nebentext fest">{k.nummer}</div> : null}
                    </td>
                    <td>
                      {k.strasse ? (
                        <>
                          {k.strasse}
                          <div className="nebentext">
                            {k.plz} {k.ort}
                            {k.land !== 'DE' ? ` · ${k.land}` : ''}
                          </div>
                        </>
                      ) : (
                        <span className="marker warnung">fehlt</span>
                      )}
                    </td>
                    <td className="schmal">
                      {k.typ === 'beides' ? 'Kunde/Lieferant' : k.typ === 'kunde' ? 'Kunde' : 'Lieferant'}
                    </td>
                    <td>
                      {k.email ? <div className="klein">{k.email}</div> : null}
                      {k.telefon ? <div className="klein still">{k.telefon}</div> : null}
                    </td>
                    <td className="schmal">
                      {k.zahlungszielTage !== null ? `${k.zahlungszielTage} Tage` : '—'}
                    </td>
                    {darfSchreiben ? (
                      <td className="schmal">
                        <button
                          type="button"
                          className="knopf klein"
                          onClick={() => setzeBearbeitet(k)}
                        >
                          Ändern
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {bearbeitet !== null ? (
        <KontaktDialog
          kontakt={bearbeitet === 'neu' ? null : bearbeitet}
          schliessen={() => setzeBearbeitet(null)}
          fertig={() => {
            setzeBearbeitet(null);
            liste.neuLaden();
          }}
        />
      ) : null}
    </Seite>
  );
}

// =========================================================================
//  Anlegen und Ändern
// =========================================================================

function KontaktDialog({
  kontakt,
  schliessen,
  fertig,
}: {
  kontakt: Kontakt | null;
  schliessen: () => void;
  fertig: () => void;
}): ReactNode {
  const vorgang = useVorgang();

  const [typ, setzeTyp] = useState<KontaktTyp>(kontakt?.typ ?? 'beides');
  const [name, setzeName] = useState(kontakt?.name ?? '');
  const [zusatz, setzeZusatz] = useState(kontakt?.zusatz ?? '');
  const [inhaber, setzeInhaber] = useState(kontakt?.inhaber ?? '');
  const [strasse, setzeStrasse] = useState(kontakt?.strasse ?? '');
  const [plz, setzePlz] = useState(kontakt?.plz ?? '');
  const [ort, setzeOrt] = useState(kontakt?.ort ?? '');
  const [land, setzeLand] = useState(kontakt?.land ?? 'DE');
  const [email, setzeEmail] = useState(kontakt?.email ?? '');
  const [telefon, setzeTelefon] = useState(kontakt?.telefon ?? '');
  const [ustId, setzeUstId] = useState(kontakt?.ustId ?? '');
  const [iban, setzeIban] = useState(kontakt?.iban ?? '');
  const [zahlungsziel, setzeZahlungsziel] = useState(
    kontakt?.zahlungszielTage !== null && kontakt?.zahlungszielTage !== undefined
      ? String(kontakt.zahlungszielTage)
      : '',
  );
  const [notiz, setzeNotiz] = useState(kontakt?.notiz ?? '');

  async function speichern(): Promise<void> {
    // Leere Felder gehen als null, nicht als leerer String: In der
    // Datenbank ist „nicht angegeben" etwas anderes als „leer".
    const oderNull = (wert: string): string | null => (wert.trim() === '' ? null : wert.trim());

    const daten = {
      typ,
      name: name.trim(),
      zusatz: oderNull(zusatz),
      inhaber: oderNull(inhaber),
      strasse: oderNull(strasse),
      plz: oderNull(plz),
      ort: oderNull(ort),
      land: land.trim().toUpperCase() || 'DE',
      email: oderNull(email),
      telefon: oderNull(telefon),
      ustId: oderNull(ustId),
      iban: oderNull(iban),
      zahlungszielTage: zahlungsziel.trim() === '' ? null : Number.parseInt(zahlungsziel, 10),
      notiz: oderNull(notiz),
    };

    const ergebnis = await vorgang.fuehreAus(() =>
      kontakt
        ? api.aendern(`/api/kontakte/${kontakt.id}`, daten)
        : api.senden('/api/kontakte', daten),
    );
    if (ergebnis !== undefined) fertig();
  }

  return (
    <Dialog
      titel={kontakt ? `${kontakt.name} ändern` : 'Kontakt anlegen'}
      breit
      schliessen={schliessen}
      fuss={
        <>
          <button type="button" className="knopf" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf haupt"
            disabled={name.trim() === '' || vorgang.laeuft}
            onClick={() => void speichern()}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stapel">
        <Fehlermeldung text={vorgang.fehler} />

        <div className="formularraster">
          <Textfeld beschriftung="Name" wert={name} setzeWert={setzeName} pflicht breit maxLaenge={200} />
          <Textfeld
            beschriftung="Zusatz"
            wert={zusatz}
            setzeWert={setzeZusatz}
            breit
            hinweis="Abteilung, Ansprechpartner, „z. Hd. …"
          />
          <Textfeld
            beschriftung="Inhaber"
            wert={inhaber}
            setzeWert={setzeInhaber}
            breit
            maxLaenge={200}
            hinweis="Die Person hinter der Firma. Beim Schreiben einer Rechnung als Empfängername wählbar."
          />
          <Auswahlfeld<KontaktTyp>
            beschriftung="Rolle"
            wert={typ}
            setzeWert={setzeTyp}
            optionen={TYPEN}
          />
          <Textfeld beschriftung="Straße und Nummer" wert={strasse} setzeWert={setzeStrasse} breit />
          <Textfeld beschriftung="PLZ" wert={plz} setzeWert={setzePlz} maxLaenge={10} />
          <Textfeld beschriftung="Ort" wert={ort} setzeWert={setzeOrt} />
          <Textfeld
            beschriftung="Land"
            wert={land}
            setzeWert={setzeLand}
            maxLaenge={2}
            hinweis="Zwei Buchstaben, etwa DE oder AT"
          />
          <Textfeld beschriftung="E-Mail" art="email" wert={email} setzeWert={setzeEmail} />
          <Textfeld beschriftung="Telefon" art="tel" wert={telefon} setzeWert={setzeTelefon} />
          <Textfeld
            beschriftung="USt-IdNr."
            wert={ustId}
            setzeWert={setzeUstId}
            hinweis="Bei Kunden im EU-Ausland"
          />
          <Textfeld
            beschriftung="IBAN"
            wert={iban}
            setzeWert={setzeIban}
            hinweis="Wird auf Prüfziffer geprüft"
          />
          <Textfeld
            beschriftung="Zahlungsziel in Tagen"
            art="number"
            wert={zahlungsziel}
            setzeWert={setzeZahlungsziel}
            klasse="zahl"
            hinweis="leer = Vorgabe aus den Stammdaten"
          />
          <Textbereich beschriftung="Notiz" wert={notiz} setzeWert={setzeNotiz} zeilen={2} breit />
        </div>

        <Meldung ton="info">
          Anschrift und Name gehen auf jede Rechnung an diesen Empfänger. Beim
          Stellen wird die Anschrift eingefroren — späterer Umzug ändert
          gestellte Rechnungen nicht.
        </Meldung>
      </div>
    </Dialog>
  );
}
